import type { NavigationGuardNext, RouteLocationNormalized, RouteRecordRaw, RouterScrollBehavior } from 'vue-router'
import type { RouterConfig } from 'nuxt/schema'
import type { ContentRoute } from '~/models/Router/Routes'
import { navigateTo } from '#app'
import { useAuthStore } from '~/stores/auth'
import VippsView from '~/views/VippsView.vue'
import VideoView from '~/views/VideoView.vue'
import UserRegisterView from '~/views/UserRegisterView.vue'
import UserActivateView from '~/views/UserActivateView.vue'
import TermExamReportView from '~/views/TermExamReportView.vue'
import TeacherArticleView from '~/views/TeacherArticleView.vue'
import TaskHeaderViewFlexible from '~/views/TaskHeaderViewFlexible.vue'
import TaskHeaderView from '~/views/TaskHeaderView.vue'
import SubjectView from '~/views/SubjectView.vue'
import SceneView from '~/views/SceneView.vue'
import ReportView from '~/views/ReportView.vue'
import ProductView from '~/views/ProductView.vue'
import PresentationView from '~/views/PresentationView.vue'
import PackageView from '~/views/PackageView.vue'
import OidcView from '~/views/OidcView.vue'
import NotFoundView from '~/views/NotFoundView.vue'
import LoginView from '~/views/LoginView.vue'
import LinkView from '~/views/LinkView.vue'
import HomeView from '~/views/HomeView.vue'
import GlobalSearchView from '~/views/GlobalSearchView.vue'
import GenericView from '~/views/GenericView.vue'
import FormWriterView from '~/views/FormWriterView.vue'
import FlytTaskView from '~/views/FlytTaskView.vue'
import FlashcardsView from '~/views/FlashcardsView.vue'
import FileView from '~/views/FileView.vue'
import FileCoverView from '~/views/FileCoverView.vue'
import FavoriteItemView from '~/views/FavoriteItemView.vue'
import ConversationView from '~/views/ConversationView.vue'
import CallbackView from '~/views/CallbackView.vue'
import BookReaderView from '~/views/BookReaderView.vue'
import BookReaderSummaryView from '~/views/BookReaderSummaryView.vue'
import AudioView from '~/views/AudioView.vue'
import AssignedTaskView from '~/views/AssignedTaskView.vue'
import ArticleView from '~/views/ArticleView.vue'
import LegacySubjectView from '~/components/subject/LegacySubjectView.vue'
import HeaderView from '~/components/subject/HeaderView.vue'
import ProductHeaderView from '~/components/products/ProductHeaderView.vue'

const redirectRoutes = [
  {
    path: '/minside',
    redirect: () => ({ name: 'home' }),
  },
  {
    path: '/nn/minside',
    redirect: () => ({ name: 'home' }),
  },
  {
    path: '/sommer',
    redirect: () => ({ name: 'subject', params: { subject: 'tve' } }),
  },
  {
    path: '/hoest',
    redirect: () => ({ name: 'subject', params: { subject: 'tve' } }),
  },
  {
    path: `/${encodeURI('høst')}`,
    alias: '/høst',
    redirect: () => ({ name: 'subject', params: { subject: 'tve' } }),
  },
  {
    path: '/vinter',
    redirect: () => ({ name: 'subject', params: { subject: 'tve' } }),
  },
  {
    path: '/vaar',
    redirect: () => ({ name: 'subject', params: { subject: 'tve' } }),
  },
  {
    path: `/${encodeURI('vår')}`,
    alias: '/vår',
    redirect: () => ({ name: 'subject', params: { subject: 'tve' } }),
  },
  {
    path: '/log_in',
    redirect: () => ({ name: 'home' }),
  },
]

const authenticationRoutes: RouteRecordRaw[] = [
  {
    path: '/oidc',
    name: 'oidc',
    component: OidcView,
    meta: { banner: false },
  },
  {
    path: '/callback',
    name: 'callback',
    component: CallbackView,
    meta: { banner: false },
  },
  {
    path: '/vipps',
    name: 'vipps',
    component: VippsView,
    meta: { banner: false },
  },
]

const previewRoutes: RouteRecordRaw[] = [
  {
    path: '/innhold/presentasjon/:locationId(\\d+)-:slug([^/]+)',
    alias: '/innhold/presentasjon/:locationId(\\d+):slug($)',
    name: 'presentation',
    meta: { hideAppFooter: true, hideAppHeader: true },
    props: ({ params }) => ({
      locationId: Number(params.locationId),
    }),
    component: PresentationView,
  },
  {
    path: '/innhold/presentasjon/:locationId(\\d+)-:slug([^/]+)/:pageNumber(\\d+)',
    alias: '/innhold/presentasjon/:locationId(\\d+):slug(?=\\/)/:pageNumber(\\d+)',
    name: 'presentation-page',
    meta: { hideAppFooter: true, hideAppHeader: true },
    props: ({ params }) => ({
      locationId: Number(params.locationId),
      pageNumber: Number(params.pageNumber),
    }),
    component: PresentationView,
  },
  {
    path: '/innhold/bokleser/:locationId(\\d+)-:slug([^/]+)',
    alias: '/innhold/bokleser/:locationId(\\d+):slug($)',
    name: 'bookreader',
    component: BookReaderView,
    meta: { banner: false, hideAppHeader: true, hideAppFooter: true },
    props: ({ params }) => ({
      locationId: Number(params.locationId)
    })
  },
  {
    path: '/innhold/bokleser/:locationId(\\d+)-:slug([^/]+)/sammendrag',
    alias: '/innhold/bokleser/:locationId(\\d+):slug(?=\\/)/sammendrag',
    name: 'bookreader_summary',
    component: BookReaderSummaryView,
    meta: { banner: false, hideAppHeader: true, hideAppFooter: true },
    props: ({ params }) => ({
      locationId: Number(params.locationId)
    })
  },
  {
    path: '/innhold/chat/:locationId(\\d+)-:slug([^/]+)',
    alias: '/innhold/chat/:locationId(\\d+):slug($)',
    name: 'chatbot',
    component: () => import('~/views/ChatView.vue'),
    meta: { banner: false, hideAppHeader: true, hideAppFooter: true },
    props: ({ params }) => ({
      locationId: Number(params.locationId)
    })
  },
  {
    path: '/innhold/samtale/:locationId(\\d+)-:slug([^/]+)',
    alias: '/innhold/samtale/:locationId(\\d+):slug($)',
    name: 'conversation',
    component: ConversationView,
    meta: { banner: false, hideAppHeader: true, hideAppFooter: true },
    props: ({ params }) => ({
      locationId: Number(params.locationId)
    })
  },
  {
    path: '/innhold/oppgave/:locationId(\\d+)-:slug([^/]+)',
    alias: '/innhold/oppgave/:locationId(\\d+):slug($)',
    name: 'flyt_task',
    meta: { banner: false, hideAppHeader: true, hideAppFooter: true },
    props: ({ params }) => ({
      locationId: Number(params.locationId),
    }),
    component: FlytTaskView,
  },
  {
    path: '/innhold/video/:locationId(\\d+)-:slug([^/]+)',
    alias: '/innhold/video/:locationId(\\d+):slug($)',
    name: 'video',
    component: VideoView,
    meta: { banner: false, hideAppHeader: true, hideAppFooter: true },
    props: ({ params }) => ({
      locationId: Number(params.locationId)
    })
  },
  {
    path: '/innhold/lyd/:locationId(\\d+)-:slug([^/]+)',
    alias: '/innhold/lyd/:locationId(\\d+):slug($)',
    name: 'audio',
    component: AudioView,
    meta: { banner: false, hideAppHeader: true, hideAppFooter: true },
    props: ({ params }) => ({
      locationId: Number(params.locationId)
    })
  },
  {
    path: '/innhold/flashcards/:locationId(\\d+)-:slug([^/]+)',
    alias: '/innhold/flashcards/:locationId(\\d+):slug($)',
    name: 'flashcards',
    component: FlashcardsView,
    meta: { banner: false, hideAppHeader: true, hideAppFooter: true },
    props: ({ params }) => ({
      locationId: Number(params.locationId)
    })
  },
  {
    path: '/innhold/tekst/:locationId(\\d+)-:slug([^/]+)',
    alias: '/innhold/tekst/:locationId(\\d+):slug($)',
    name: 'article',
    component: ArticleView,
    meta: { banner: false, hideAppHeader: true, hideAppFooter: true },
    props: ({ params }) => ({
      locationId: Number(params.locationId),
      slug: String(params.slug),
    })
  },
  {
    path: '/innhold/tekst/:locationId(\\d+)-:slug([^/]+)/:subLocationId(\\d+)-:subSlug',
    alias: '/innhold/tekst/:locationId(\\d+):slug([^/]*)/:subLocationId(\\d+):subSlug($)',
    name: 'article_sub',
    component: ArticleView,
    meta: { banner: false, hideAppHeader: true, hideAppFooter: true },
    props: ({ params }) => ({
      locationId: Number(params.locationId),
      slug: String(params.slug),
      subLocationId: Number(params?.subLocationId)
    })
  },
  {
    path: '/innhold/dokument/:locationId(\\d+)-:slug([^/]+)',
    alias: '/innhold/dokument/:locationId(\\d+):slug($)',
    name: 'pdf',
    component: () => import('~/views/PdfView.vue'),
    meta: { banner: false, hideAppHeader: true, hideAppFooter: true },
    props: ({ params }) => ({
      locationId: Number(params.locationId)
    })
  },
  {
    path: '/innhold/pakke/:locationId(\\d+)-:slug([^/]+)',
    alias: '/innhold/pakke/:locationId(\\d+):slug($)',
    name: 'package',
    component: PackageView,
    meta: { banner: false, hideAppHeader: true, isNavigation: true },
    props: ({ params }) => ({
      locationId: Number(params.locationId)
    })
  },
  {
    path: '/innhold/produkt/:productLocationId(\\d+)-:productSlug([^/]+)/pakke/:locationId(\\d+)-:slug([^/]+)',
    alias: '/innhold/produkt/:productLocationId(\\d+):productSlug([^/]*)/pakke/:locationId(\\d+):slug($)',
    name: 'product_package',
    component: PackageView,
    meta: { banner: false, hideAppHeader: true, isNavigation: true },
    props: ({ params }) => ({
      locationId: Number(params.locationId),
    })
  },
  {
    path: '/innhold/fil/:locationId(\\d+)-:slug([^/]+)',
    alias: '/innhold/fil/:locationId(\\d+):slug($)',
    name: 'file',
    component: FileView,
    meta: { banner: false, hideAppHeader: true, hideAppFooter: true },
    props: ({ params }) => ({
      locationId: Number(params.locationId)
    })
  },
  {
    path: '/innhold/omslag/:locationId(\\d+)-:slug([^/]+)',
    alias: '/innhold/omslag/:locationId(\\d+):slug($)',
    name: 'file_cover',
    component: FileCoverView,
    meta: { banner: false, hideAppHeader: true, hideAppFooter: true },
    props: ({ params }) => ({
      locationId: Number(params.locationId)
    })
  },
  {
    path: '/innhold/link/:locationId(\\d+)-:slug([^/]+)',
    alias: '/innhold/link/:locationId(\\d+):slug($)',
    name: 'link',
    component: LinkView,
    meta: { banner: false, hideAppHeader: true, hideAppFooter: true },
    props: ({ params }) => ({
      locationId: Number(params.locationId)
    })
  },
  {
    path: '/innebygd/:locationId(\\d+)-:slug([^/]+)',
    alias: '/innebygd/:locationId(\\d+):slug($)',
    name: 'iframe',
    component: () => import('~/views/IframeView.vue'),
    meta: { banner: false, hideAppHeader: true, hideAppFooter: true },
    props: ({ params }) => ({
      locationId: Number(params.locationId)
    })
  },
  {
    path: '/innhold/scene/:locationId(\\d+)-:slug([^/]+)',
    alias: '/innhold/scene/:locationId(\\d+):slug($)',
    name: 'scene',
    component: SceneView,
    meta: { banner: false, hideAppHeader: true, hideAppFooter: true },
    props: ({ params }) => ({
      locationId: Number(params.locationId),
    })
  },
  {
    path: '/innhold/skriveramme/:locationId(\\d+)-:slug([^/]+)',
    alias: '/innhold/skriveramme/:locationId(\\d+):slug($)',
    name: 'form_writer',
    component: FormWriterView,
    meta: { banner: false, hideAppHeader: true, hideAppFooter: true },
    props: ({ params }) => ({
      locationId: Number(params.locationId)
    })
  },
  {
    path: `/innhold/${encodeURI('lærer')}/:locationId(\\d+)-:slug([^/]+)`,
    alias: '/innhold/lærer/:locationId(\\d+):slug($)',
    name: 'teacher_article',
    component: TeacherArticleView,
    meta: {
      hideAppFooter: true,
      hideAppHeader: true,
      middleware: () => {
        const { isTeacher } = useAuthStore()
        if (!isTeacher)
          return navigateTo({ name: 'notfound' })
      }
    },
    props: ({ params }) => ({
      locationId: Number(params.locationId)
    })
  },
  {
    path: '/innhold/kolofon/:locationId(\\d+)-:slug([^/]+)',
    alias: '/innhold/kolofon/:locationId(\\d+):slug($)',
    name: 'colophon',
    component: () => import('~/views/ColophonLocationView.vue'),
    meta: { hideAppFooter: true, hideAppHeader: true },
    props: ({ params }) => ({
      locationId: Number(params.locationId)
    })
  },
  {
    path: '/fag/:subject/:locationId(\\d+)-:slug([^/]+)',
    alias: '/fag/:subject/:locationId(\\d+):slug($)',
    name: 'header',
    component: HeaderView,
    meta: { banner: true, isNavigation: true },
    props: ({ params }) => ({
      subjectCode: String(params.subject).toUpperCase(),
      headerLocationId: Number(params.locationId),
    })
  },
  {
    path: '/innhold/produkt/:locationId(\\d+)-:slug([^/]+)',
    alias: '/innhold/produkt/:locationId(\\d+):slug($)',
    name: 'product',
    component: ProductView,
    meta: { banner: true, preview: true, isNavigation: true },
    props: ({ params }) => ({
      locationId: Number(params.locationId)
    })
  },
  {
    path: '/innhold/produkt/:productId(\\d+)-:productSlug([^/]+)/:locationId(\\d+)-:slug([^/]+)',
    alias: '/innhold/produkt/:productId(\\d+):productSlug([^/]*)/:locationId(\\d+):slug($)',
    name: 'product_header',
    component: ProductHeaderView,
    meta: { banner: true, preview: true, isNavigation: true },
    props: ({ params }) => ({
      productId: Number(params.productId),
      locationId: Number(params.locationId),
    })
  },
  {
    path: '/enhet/:itemId',
    name: 'favorite_item',
    component: FavoriteItemView,
    meta: { banner: false, hideAppHeader: true },
    props: ({ params }) => ({
      itemId: String(params.itemId)
    })
  },
]

const otherRoutes: RouteRecordRaw[] = [
  {
    path: '/',
    name: 'home',
    component: HomeView,
    meta: { banner: true },
  },
  {
    path: '/ikke-funnet',
    name: 'notfound',
    component: NotFoundView,
    meta: { banner: false },
  },
  {
    path: '/fag/:subject',
    name: 'subject',
    component: SubjectView,
    meta: { banner: true },
    props: ({ params }: { params: { subject: string } }) => ({
      subjectCode: params.subject.toUpperCase()
    })
  },
  {
    path: '/fag/:subject/beta',
    name: 'subject_upcoming',
    component: SubjectView,
    meta: { banner: true },
    props: ({ params }: { params: { subject: string } }) => ({
      subjectCode: params.subject.toUpperCase(),
      isUpcoming: true,
      isArchive: false,
    })
  },
  {
    path: '/fag/:subject/arkiv',
    name: 'subject_archive',
    component: LegacySubjectView,
    meta: { banner: true },
    props: ({ params }: { params: { subject: string } }) => ({
      subjectCode: params.subject.toUpperCase(),
      isUpcoming: false,
      isArchive: true,
    })
  },
  {
    path: '/innhold/produkt/:productLocationId(\\d+)-:productSlug([^/]+)/prove/:parentLocationId(\\d+)-:parentSlug([^/]+)/:locationId(\\d+)-:slug',
    alias: '/innhold/produkt/:productLocationId(\\d+):productSlug([^/]*)/prove/:parentLocationId(\\d+):parentSlug([^/]*)/:locationId(\\d+):slug($)',
    name: 'task_header',
    component: TaskHeaderView,
    meta: { banner: true },
    props: ({ params }) => ({
      locationId: Number(params.locationId),
    })
  },
  {
    path: '/innhold/produkt/:productLocationId(\\d+)-:productSlug([^/]+)/prove/:parentLocationId(\\d+)-:parentSlug([^/]+)/:locationId(\\d+)-:slug/rediger',
    alias: '/innhold/produkt/:productLocationId(\\d+):productSlug([^/]*)/prove/:parentLocationId(\\d+):parentSlug([^/]*)/:locationId(\\d+):slug([^/]*)/rediger',
    name: 'task_header_flexible',
    component: TaskHeaderViewFlexible,
    meta: { banner: true },
    props: ({ params }) => ({
      locationId: Number(params.locationId),
    })
  },
  {
    path: '/prove/:contentType/:taskId/:locationId(\\d+)-:slug([^/]+)',
    name: 'assigned_task_view',
    component: AssignedTaskView,
    meta: { banner: false, hideAppHeader: true, hideAppFooter: true },
    props: ({ params }) => ({
      locationId: Number(params.locationId),
      contentType: String(params.contentType),
    })
  },
  {
    path: `/fag/:subject/${encodeURI('lærer')}/:grade/:contentId?`,
    alias: '/fag/:subject/lærer/:grade',
    name: 'teacher_subject',
    component: () => import('~/views/TeacherSubjectView.vue'),
    meta: { hideAppFooter: true, hideAppHeader: true },
    props: ({ params }: { params: { subject: string, grade: string, contentId?: string } }) => ({
      subjectCode: params.subject.toUpperCase(),
      gradeCode: params.grade.toLowerCase(),
      contentId: params.contentId ? Number(params.contentId) : undefined
    })
  },
  {
    path: '/fag/:subject/laerer',
    name: 'teacher_subject_legacy',
    component: () => import('~/views/TeacherSubjectLegacyView.vue'),
    meta: { banner: true },
    props: ({ params }: { params: { subject: string } }) => ({
      subjectCode: params.subject.toUpperCase()
    })
  },
  {
    path: '/favoritter',
    name: 'favorites',
    redirect: () => ({
      name: 'my_content'
    }),
  },
  {
    path: '/favoritter/:listId',
    name: 'favorite_list',
    redirect: to => ({
      name: 'favorite_list',
      params: { listId: to.params.listId }
    })
  },
  {
    path: '/innhold/kolofon/:subject/:grade',
    name: 'colophon_subject',
    component: () => import('~/views/ColophonView.vue'),
    meta: { hideAppFooter: true, hideAppHeader: true },
    props: ({ params }: { params: { subject: string, grade: string } }) => ({
      subjectCode: params.subject.toUpperCase(),
      gradeCode: params.grade.toLowerCase(),
    })
  },
  {
    path: '/logg-inn',
    name: 'login',
    component: LoginView,
    meta: { banner: false, hideAppHeader: true },
  },
  {
    path: '/sok',
    name: 'search',
    component: GlobalSearchView,
    meta: { banner: false, headerColor: true },
  },
  {
    path: '/terminprove-rapport',
    name: 'term-exam-report',
    component: TermExamReportView,
    meta: { banner: true }
  },
  {
    path: '/registrer',
    name: 'register',
    component: UserRegisterView,
    meta: { banner: false, hideAppHeader: true },
  },
  {
    path: '/aktiver',
    name: 'activate',
    component: UserActivateView,
    meta: { banner: false, hideAppHeader: true },
  },
  {
    path: '/mitt-innhold',
    name: 'my_content',
    component: () => import('~/views/MyContentTeacherView.vue'),
    meta: {
      banner: false,
      headerColor: true,
      middleware: (to) => {
        const { isTeacher } = useAuthStore()
        if (!isTeacher)
          return navigateTo({ name: 'from_teacher', query: to.query })
      },
    },
    children: [
      {
        path: 'lister',
        name: 'my_content_lists',
        component: () => import('~/components/favorites/FavoritesLists.vue')
      },
      {
        path: 'delt',
        name: 'my_content_shared',
        component: () => import('~/components/shared/SharedContentTeacher.vue')
      },
      {
        path: 'rapporter',
        name: 'my_content_reports',
        component: ReportView,
      }
    ],
    redirect: { name: 'my_content_lists' }
  },
  {
    path: '/lister/:listId',
    name: 'favorite_list',
    component: () => import('~/views/FavoriteListView.vue'),
    meta: { banner: true },
    props: true,
  },
  {
    path: '/lister/bla-i/:listId',
    name: 'favorite_list_navigation',
    component: () => import('~/views/FavoriteNavigationView.vue'),
    meta: { hideAppFooter: true, hideAppHeader: true },
    props: ({ params }: { params: { listId: string } }) => ({
      listId: params.listId,
    }),
    children: [...previewRoutes.map(route => ({
      ...route,
      ...(route.alias && { alias: route.alias?.slice(1) }),
      path: route.path.slice(1),
      name: `${String(route.name)}_favorite`,
    })), ...[{
      path: 'fag/:subject',
      name: 'subject_favorite',
      component: SubjectView,
      meta: { banner: true },
      props: ({ params }: { params: { subject: string } }) => ({
        subjectCode: params.subject.toUpperCase()
      })
    }]],
  },
  {
    path: '/fra-laerer',
    name: 'from_teacher',
    component: () => import('~/views/MyContentStudentView.vue'),
    meta: {
      banner: false,
      headerColor: true,
      middleware: () => {
        const { isTeacher } = useAuthStore()
        if (isTeacher)
          return navigateTo({ name: 'my_content' })
      },
    },
  },
]

const routes: RouteRecordRaw[] = [
  ...authenticationRoutes,
  ...redirectRoutes,
  ...otherRoutes.map(view => ({ ...view, meta: { ...view.meta, isNavigation: true } })),
  ...previewRoutes.map(view => ({ ...view, meta: { ...view.meta, preview: true } })),
  {
    path: '/:path(.*)*',
    name: 'generic',
    component: GenericView,
    meta: {
      banner: false,
      middleware: (to, from) => {
        to.params.returnPath = from.fullPath
      },
    },
  }
]

export default {
  routes: () => routes,
} satisfies RouterConfig
